import type { IHubCatalog, IHubPackage } from "@models";
import { findHubCatalogForPackage } from "../pages/catalog/hubPackageCatalogMatch";

export function isSolutionCatalog(c: IHubCatalog): boolean {
  return !c.parentCatalogIds?.length && !c.addon;
}

export function isAddonCatalog(c: IHubCatalog): boolean {
  return Boolean(c.parentCatalogIds?.length);
}

/** Sort bucket: 0 = solution, 1 = add-on, 2 = unknown / no catalog */
export function hubRowSortGroup(catalog: IHubCatalog | undefined): 0 | 1 | 2 {
  if (!catalog) return 2;
  if (isSolutionCatalog(catalog)) return 0;
  return 1;
}

export interface IOrderedCartEntry {
  pkg: IHubPackage;
  rowIndex: number;
  catalog: IHubCatalog | undefined;
  group: 0 | 1 | 2;
  /** Add-on row sits under a solution that is in the cart. */
  matchedToSolutionInCart?: boolean;
}

export function checkoutCartLineKey(pkg: IHubPackage): string {
  const id = pkg.packageId;
  const rel0 = pkg.catalogRelations?.[0];
  if (id != null && id > 0 && rel0 != null) {
    return `pkg-${id}-cat-${rel0.catalogId}-cv${rel0.catalogVersion}`;
  }
  if (id != null && id > 0) return `pkg-${id}`;
  return `${pkg.name}-${pkg.label}`;
}

/**
 * Solution row, then its add-ons, then orphan add-ons, then unknown lines
 * (same order as checkout).
 */
export function getOrderedCartEntries(
  rows: IHubPackage[],
  catalogs: IHubCatalog[],
): IOrderedCartEntry[] {
  const entries: IOrderedCartEntry[] = rows.map((pkg, rowIndex) => {
    const catalog = findHubCatalogForPackage(catalogs, pkg);
    return {
      pkg,
      rowIndex,
      catalog,
      group: hubRowSortGroup(catalog),
    };
  });

  const solutions = entries.filter((e) => e.group === 0);
  const addons = entries.filter((e) => e.group === 1);
  const unknown = entries.filter((e) => e.group === 2);

  const addonMatched = new Set<number>();
  const ordered: IOrderedCartEntry[] = [];

  for (const sol of solutions) {
    ordered.push(sol);
    const sid = sol.catalog?.catalogId;
    if (sid == null || sid <= 0) continue;
    for (const addon of addons) {
      if (addonMatched.has(addon.rowIndex)) continue;
      const parents = addon.catalog?.parentCatalogIds ?? [];
      if (parents.includes(sid)) {
        ordered.push({ ...addon, matchedToSolutionInCart: true });
        addonMatched.add(addon.rowIndex);
      }
    }
  }

  const orphanAddons = addons
    .filter((a) => !addonMatched.has(a.rowIndex))
    .sort((a, b) => a.rowIndex - b.rowIndex);
  for (const a of orphanAddons) {
    ordered.push({ ...a, matchedToSolutionInCart: false });
  }

  unknown.sort((a, b) => a.rowIndex - b.rowIndex);
  ordered.push(...unknown);

  return ordered;
}

/** Matches checkout card rail: indents add-ons; solutions/unknown stay full width. */
export function getCheckoutCartEntryOuterClassName(
  group: 0 | 1 | 2,
  matchedToSolutionInCart: boolean | undefined,
): string {
  if (group === 1) {
    return matchedToSolutionInCart === false
      ? "ml-8 w-[calc(100%-2rem)] border-l-2 border-dashed border-muted-foreground/40 pl-5 sm:ml-10 sm:w-[calc(100%-2.5rem)] sm:pl-6"
      : "ml-6 w-[calc(100%-1.5rem)] border-l-2 border-muted-foreground/25 pl-4 sm:ml-8 sm:w-[calc(100%-2rem)] sm:pl-5";
  }
  return "";
}

export function getCheckoutCartSectionHeadings(
  ordered: IOrderedCartEntry[],
  orderedIndex: number,
): {
  showNestedAddonsHeading: boolean;
  showOtherAddonsHeading: boolean;
} {
  const entry = ordered[orderedIndex];
  const prev = ordered[orderedIndex - 1];
  const previousEntryGroup = prev?.group;
  const previousAddonMatchedToSolution =
    prev?.group === 1 ? prev.matchedToSolutionInCart : undefined;
  const { group, matchedToSolutionInCart } = entry;

  return {
    showNestedAddonsHeading:
      group === 1 &&
      matchedToSolutionInCart !== false &&
      (previousEntryGroup === undefined || previousEntryGroup !== 1),
    showOtherAddonsHeading:
      group === 1 &&
      matchedToSolutionInCart === false &&
      (previousEntryGroup === undefined ||
        previousEntryGroup !== 1 ||
        previousAddonMatchedToSolution === true),
  };
}
